import React, { useState, useEffect, useRef } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { InputNumber } from 'primereact/inputnumber';
import './AddEditPermitRenewal.css';
import { handleGetRequest } from '../../services/GetTemplate';
import { handlePostRequest } from '../../services/PostTemplate';
import { useDispatch } from 'react-redux';
import { handlePutRequest } from '../../services/PutTemplate';
import { useParams, useNavigate } from 'react-router-dom';
import { baseURL } from '../../Config';

function AddEditPermitRenewal({ mode = 'add' }) {
    const { id } = useParams();
    const navigate = useNavigate();
    const [permitData, setPermitData] = useState(null);
    const [isEditing, setIsEditing] = useState(mode === 'edit');
    const [isViewMode, setIsViewMode] = useState(mode === 'view');
    const [imagePreview, setImagePreview] = useState(null);
    const [cnicFrontSide, setCnicFrontSide] = useState(null);
    const [cnicBackSide, setCnicBackSide] = useState(null);
    const [vehicleRegistration, setVehicleRegistration] = useState(null);
    const [fitnessCertificate, setFitnessCertificate] = useState(null);
    const [token, setToken] = useState(null);
    const [affidavit, setAffidavit] = useState(null);
    const fileInputRef = useRef(null);
    const dispatch = useDispatch();
    const [applicantTypes, setApplicantTypes] = useState([]);
    const [vehicleTypes, setVehicleTypes] = useState([]);
    const [model, setModel] = useState([]);
    const [error, setError] = useState(null);
    const [status, setStatus] = useState(null);

    const roleName = localStorage.getItem('roleName');
    const formik = useFormik({
        initialValues: {
            phoneNumber: permitData?.phoneNumber || '',
            applicantName: permitData?.applicantName || '',
            applicantType: permitData?.applicantType || '',
            applicantCNIC: permitData?.applicantCNIC || '',
            applicantAddress: permitData?.applicantAddress || '',
            vehicleType: permitData?.vehicleType || '',
            registrationNo: permitData?.registrationNo || '',
            engineNo: permitData?.engineNo || '',
            chassisNo: permitData?.chassisNo || '',
            model: permitData?.model || '',
            maker: permitData?.maker || '',
            cnicFrontSide: null,
            cnicFrontSideName: '',
            cnicBackSide: null,
            cnicBackSideName: '',
            vehicleRegistration: null,
            vehicleRegistrationName: '',
            fitnessCertificate: null,
            fitnessCertificateName: '',
            token: null,
            tokenName: '',
            affidavit: null,
            affidavitName: '',
            permitFee: permitData?.permitFee || 0,
            permitFine: permitData?.permitFine || 0,
            totalPermitFee: permitData?.totalPermitFee || 0,
            date: new Date().toISOString().split('T')[0]
        },
        validate: (data) => {
            let errors = {};
            if (!data.phoneNumber) {
                errors.phoneNumber = 'Phone number is required';
            } else if (!/^[0-9]+$/.test(data.phoneNumber)) {
                errors.phoneNumber = 'Phone number must contain only numbers';
            } else if (!/^[0-9]{11}$/.test(data.phoneNumber)) {
                errors.phoneNumber = 'Phone number must be 11 digits';
            }

            if (!data.applicantName) {
                errors.applicantName = 'Applicant Name is required';
            } else if (!/^[a-zA-Z\s]+$/.test(data.applicantName)) {
                errors.applicantName = 'Name can only contain letters';
            }

            if (!data.applicantType) {
                errors.applicantType = 'Applicant Type is required';
            }

            if (!data.applicantCNIC) {
                errors.applicantCNIC = 'CNIC is required';
            } else if (!/^[0-9]+$/.test(data.applicantCNIC)) {
                errors.applicantCNIC = 'CNIC must contain only numbers';
            } else if (!/^[0-9]{13}$/.test(data.applicantCNIC)) {
                errors.applicantCNIC = 'CNIC must be 13 digits';
            }

            if (!data.applicantAddress) {
                errors.applicantAddress = 'Address is required';
            }

            if (!data.vehicleType) {
                errors.vehicleType = 'Vehicle Category is required';
            }

            if (!data.registrationNo) {
                errors.registrationNo = 'Registration No. is required';
            } else if (!/^[a-zA-Z0-9]+$/.test(data.registrationNo)) {
                errors.registrationNo = 'Registration No. cannot contain special characters';
            }

            if (!data.engineNo) {
                errors.engineNo = 'Engine No. is required';
            } else if (!/^[a-zA-Z0-9]+$/.test(data.engineNo)) {
                errors.engineNo = 'Engine No. cannot contain special characters';
            }

            if (!data.chassisNo) {
                errors.chassisNo = 'Chassis No. is required';
            } else if (!/^[a-zA-Z0-9]+$/.test(data.chassisNo)) {
                errors.chassisNo = 'Chassis No. cannot contain special characters';
            }

            if (!data.model) {
                errors.model = 'Model is required';
            }

            if (!data.maker) {
                errors.maker = 'Maker is required';
            }

            if (data.permitFee === undefined || data.permitFee === '') {
                errors.permitFee = 'Permit Fee is required';
            } else if (isNaN(data.permitFee)) {
                errors.permitFee = 'Permit Fee must be a number';
            }

            if (data.permitFine === undefined || data.permitFine === '') {
                errors.permitFine = 'Permit Fine is required';
            } else if (isNaN(data.permitFine)) {
                errors.permitFine = 'Permit Fine must be a number';
            }

            if (data.totalPermitFee === undefined || data.totalPermitFee === '') {
                errors.totalPermitFee = 'Total Permit Fee is required';
            } else if (isNaN(data.totalPermitFee)) {
                errors.totalPermitFee = 'Total Permit Fee must be a number';
            }

            if (!data.cnicFrontSide) {
                errors.cnicFrontSide = 'CNIC Front Side is required';
            }

            if (!data.cnicBackSide) {
                errors.cnicBackSide = 'CNIC Back Side is required';
            }

            if (!data.vehicleRegistration) {
                errors.vehicleRegistration = 'Vehicle Registration Book is required';
            }

            if (!data.fitnessCertificate) {
                errors.fitnessCertificate = 'Fitness Certificate is required';
            }

            if (!data.token) {
                errors.token = 'Token is required';
            }

            if (!data.affidavit) {
                errors.affidavit = 'Affidavit is required';
            }

            if (!data.date) {
                errors.date = 'Date is required';
            }

            return errors;
        },
        onSubmit: async (data) => {
            try {
                // Validate all required fields are present
                if (!formik.isValid) {
                    return;
                }

                // Create FormData object
                const formData = new FormData();
                formData.append('Name', data.applicantName);
                formData.append('ApplicantType', data.applicantType);
                formData.append('MobileNumber', data.phoneNumber);
                formData.append('CNIC', data.applicantCNIC);
                formData.append('Address', data.applicantAddress);
                formData.append('VehicleType', data.vehicleType);
                formData.append('RegisterationNumber', data.registrationNo);
                formData.append('EngineNumber', data.engineNo);
                formData.append('ChassisNumber', data.chassisNo);
                formData.append('Model', data.model);
                formData.append('Maker', data.maker);
                formData.append('RegisterationFee', Number(data.permitFee));
                formData.append('FineAmount', Number(data.permitFine));
                formData.append('TotalAmount', Number(data.permitFee) + Number(data.permitFine));
                formData.append('RegistrationDate', data.date);

                // Append all files
                formData.append('IdCard', cnicFrontSide);
                formData.append('IdCardBack', cnicBackSide);
                formData.append('RegistrationBook', vehicleRegistration);
                formData.append('FitnessCertificate', fitnessCertificate);
                formData.append('Token', token);
                formData.append('Affidavit', affidavit);

                // Make the API request
                const response = await fetch(`${baseURL}/api/Request/Add`, {
                    method: 'POST',
                    body: formData,
                    headers: {
                        // Don't set Content-Type header - it will be automatically set with the boundary
                        Authorization: `Bearer ${sessionStorage.getItem('token')}`,
                        Accept: 'multipart/form-data'
                    }
                });

                const result = await response.json();

                if (result?.success) {
                    setTimeout(() => {
                        navigate('/permit-requests');
                    }, 1000);
                } else {
                }
            } catch (error) {
                console.error('Error:', error);
            }
        }
    });

    const handleImageClick = () => {
        fileInputRef.current?.click();
    };

    const handleImageChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            formik.setFieldValue('photo', file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleFileChange = (event, fieldName) => {
        const file = event.target.files[0];
        if (file) {
            formik.setFieldValue(fieldName, file);
            formik.setFieldValue(fieldName + 'Name', file.name);

            switch (fieldName) {
                case 'cnicFrontSide':
                    setCnicFrontSide(file);
                    break;
                case 'cnicBackSide':
                    setCnicBackSide(file);
                    break;
                case 'vehicleRegistration':
                    setVehicleRegistration(file);
                    break;
                case 'fitnessCertificate':
                    setFitnessCertificate(file);
                    break;
                case 'token':
                    setToken(file);
                    break;
                case 'affidavit':
                    setAffidavit(file);
                    break;
                default:
                    break;
            }
        }
    };

    const getApplicantType = async () => {
        const response = await handleGetRequest('/api/dropdown/applicants', false, false);
        if (response?.success) {
            setApplicantTypes(response.data);
        } else {
            setError('Failed to fetch applicant types');
        }
    };

    const getVehicleType = async () => {
        const response = await handleGetRequest('/api/permit/fee', false, false);
        if (response?.success) {
            setVehicleTypes(response.data || []);
        } else {
            setError('Failed to fetch Vehicle categories');
        }
    };

    useEffect(() => {
        getApplicantType();
        getVehicleType();
        const currentYear = new Date().getFullYear();
        const years = Array.from({ length: 15 }, (_, i) => currentYear - i);
        setModel(years);
    }, []);

    useEffect(() => {
        if (id) {
            fetchPermitData();
        }
    }, [id]);

    // Handle Vehicle Category changes
    useEffect(() => {
        if (formik.values.vehicleType) {
            const vehicleType = vehicleTypes.find((type) => type.id === formik.values.vehicleType);
            if (vehicleType) {
                formik.setFieldValue('permitFee', vehicleType.registrationFee);
                // formik.setFieldValue('permitFine', vehicleType.finePerDay);
                formik.setFieldValue('totalPermitFee', Number(vehicleType.registrationFee) + Number(vehicleType.finePerDay));
            }
        }
    }, [formik.values.vehicleType, vehicleTypes]);

    useEffect(() => {
        if (formik.values.date && formik.values.vehicleType) {
            const vehicleType = vehicleTypes.find((type) => type.id === formik.values.vehicleType);
            if (vehicleType) {
                const selectedDate = new Date(formik.values.date);
                const today = new Date();

                // Set both dates to start of day for accurate day difference
                selectedDate.setHours(0, 0, 0, 0);
                today.setHours(0, 0, 0, 0);

                // Calculate difference in days
                const diffTime = Math.abs(today - selectedDate);
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                // Apply fine if more than 15 days
                if (diffDays > 15) {
                    const fineDays = diffDays - 15; // Only count days after the 15-day grace period
                    const permitFine = vehicleType.finePerDay * fineDays;
                    formik.setFieldValue('permitFine', permitFine);
                    formik.setFieldValue('totalPermitFee', Number(vehicleType.registrationFee) + Number(permitFine));
                } else {
                    formik.setFieldValue('permitFine', 0);
                    formik.setFieldValue('totalPermitFee', Number(vehicleType.registrationFee));
                }
            }
        }
    }, [formik.values.date, formik.values.vehicleType, vehicleTypes]);

    const fetchPermitData = async () => {
        try {
            const response = await handleGetRequest(`/api/permit/${id}`);
            if (response?.success) {
                setPermitData(response.data[0]);
                console.log(response.data[0].status);
                setStatus(response.data[0].status);
                formik.setValues({
                    phoneNumber: response.data[0].mobileNumber,
                    applicantName: response.data[0].name,
                    applicantType: response.data[0].applicantTypeId,
                    applicantCNIC: response.data[0].cnic,
                    applicantAddress: response.data[0].address,
                    vehicleType: response.data[0].vehicleType,
                    registrationNo: response.data[0].registerationNumber,
                    engineNo: response.data[0].engineNumber,
                    chassisNo: response.data[0].chassisNumber,
                    model: response.data[0].model,
                    maker: response.data[0].maker,
                    vehicleRegistration: response.data[0].registrationBook,
                    vehicleRegistrationName: response.data[0].registrationBook?.split('/').pop() || '',
                    fitnessCertificate: response.data[0].fitnessCertificate,
                    fitnessCertificateName: response.data[0].fitnessCertificate?.split('/').pop() || '',
                    token: response.data[0].token,
                    tokenName: response.data[0].token?.split('/').pop() || '',
                    affidavit: response.data[0].affidavit,
                    affidavitName: response.data[0].affidavit?.split('/').pop() || '',
                    cnicFrontSide: response.data[0].idCard,
                    cnicFrontSideName: response.data[0].idCard?.split('/').pop() || '',
                    cnicBackSide: response.data[0].idCardBack,
                    cnicBackSideName: response.data[0].idCardBack?.split('/').pop() || '',
                    permitFee: response.data[0].registerationFee,
                    permitFine: response.data[0].fineAmount,
                    totalPermitFee: response.data[0].totalAmount,
                    date: new Date(response.data[0].registrationDate).toISOString().split('T')[0]
                });
            }
        } catch (error) {
            console.error('Error fetching permit data:', error);
        } finally {
        }
    };

    const handleBack = () => {
        navigate('/permit-requests');
    };

    // Update the form title based on mode
    const getFormTitle = () => {
        if (isViewMode) return 'View Permit Request';
        if (isEditing) return 'Edit Permit Request';
        return 'Add New Permit Request';
    };

    // Update the submit button based on mode
    const getSubmitButtonProps = () => {
        if (isViewMode) return { label: 'Close', onClick: handleBack };
        if (isEditing) return { label: 'UPDATE', type: 'submit' };
        return { label: 'ADD', type: 'submit' };
    };

    const handleUpdateStatus = (status) => async () => {
        const response = await dispatch(handlePutRequest([], `/api/Request/UpdateStatus?id=${id}&status=${status}`));
        if (response?.success) {
            setTimeout(() => {
                navigate('/permit-requests');
            }, 1000);
        }
    };

    const isFormFieldValid = (name) => !!(formik.touched[name] && formik.errors[name]);
    const getFormErrorMessage = (name) => {
        return isFormFieldValid(name) && <small className="p-error">{formik.errors[name]}</small>;
    };

    console.log(formik.errors);

    return (
        <div className="grid">
            <div className="col-12">
                <div className="flex justify-content-between align-items-center mb-4">
                    <h2 className="text-xl font-semibold">{getFormTitle()}</h2>
                </div>

                <form onSubmit={formik.handleSubmit} className="form-container">
                    <button type="button" className="close-button" onClick={handleBack}>
                        <i className="pi pi-times" />
                    </button>

                    <div className="text-center" disabled={isViewMode}>
                        <input disabled={isViewMode} type="file" ref={fileInputRef} accept="image/*" style={{ display: 'none' }} onChange={handleImageChange} />
                        <div
                            className="photo-upload-container"
                            onClick={handleImageClick}
                            style={
                                imagePreview
                                    ? {
                                          backgroundImage: `url(${imagePreview})`,
                                          backgroundSize: 'cover',
                                          backgroundPosition: 'center'
                                      }
                                    : {}
                            }
                            disabled={isViewMode}
                        >
                            {!imagePreview && <i className="pi pi-camera" style={{ fontSize: '1.5rem' }} />}
                        </div>
                        <span className="photo-upload-text">Upload Photo</span>
                    </div>

                    <div className="form-section">
                        <h3 className="section-title">Contact Info</h3>
                        <div className="grid-3">
                            <div className="form-group">
                                <label className="form-label">Applicant Name</label>
                                <InputText
                                    id="applicantName"
                                    onBlur={formik.handleBlur}
                                    name="applicantName"
                                    value={formik.values.applicantName}
                                    onChange={formik.handleChange}
                                    className={formik.errors.applicantName ? 'p-invalid' : ''}
                                    disabled={isViewMode}
                                />
                                {getFormErrorMessage('applicantName')}
                            </div>
                            <div className="form-group">
                                <label className="form-label">Applicant Type</label>
                                <Dropdown
                                    id="applicantType"
                                    name="applicantType"
                                    onBlur={formik.handleBlur}
                                    value={formik.values.applicantType}
                                    options={applicantTypes}
                                    optionLabel="typeName"
                                    optionValue="id"
                                    onChange={formik.handleChange}
                                    className={formik.errors.applicantType ? 'p-invalid' : ''}
                                    disabled={isViewMode}
                                />

                                {getFormErrorMessage('applicantType')}
                            </div>
                            <div className="form-group">
                                <label className="form-label">Phone Number</label>
                                <InputText
                                    id="phoneNumber"
                                    name="phoneNumber"
                                    onBlur={formik.handleBlur}
                                    value={formik.values.phoneNumber || ''}
                                    onChange={formik.handleChange}
                                    className={formik.errors.phoneNumber ? 'p-invalid' : ''}
                                    placeholder="03001234567"
                                    disabled={isViewMode}
                                />
                                {getFormErrorMessage('phoneNumber')}
                            </div>
                        </div>

                        <div className="grid-3">
                            <div className="form-group">
                                <label className="form-label">Applicant CNIC</label>
                                <InputText
                                    id="applicantCNIC"
                                    name="applicantCNIC"
                                    onBlur={formik.handleBlur}
                                    value={formik.values.applicantCNIC}
                                    onChange={formik.handleChange}
                                    className={formik.errors.applicantCNIC ? 'p-invalid' : ''}
                                    disabled={isViewMode}
                                />
                                {getFormErrorMessage('applicantCNIC')}
                            </div>
                            <div className="form-group">
                                <label className="form-label">Applicant Address</label>
                                <InputText
                                    id="applicantAddress"
                                    name="applicantAddress"
                                    onBlur={formik.handleBlur}
                                    value={formik.values.applicantAddress}
                                    onChange={formik.handleChange}
                                    className={formik.errors.applicantAddress ? 'p-invalid' : ''}
                                    disabled={isViewMode}
                                />
                                {getFormErrorMessage('applicantAddress')}
                            </div>
                            <div className="form-group">
                                <label className="form-label">Vehicle Category</label>
                                <Dropdown
                                    id="vehicleType"
                                    name="vehicleType"
                                    value={formik.values.vehicleType}
                                    options={vehicleTypes}
                                    onBlur={formik.handleBlur}
                                    optionLabel="categoryName"
                                    optionValue="id"
                                    onChange={formik.handleChange}
                                    className={formik.errors.vehicleType ? 'p-invalid' : ''}
                                    disabled={isViewMode}
                                />
                                {getFormErrorMessage('vehicleType')}
                            </div>
                        </div>

                        <div className="grid-3">
                            <div className="form-group">
                                <label className="form-label">Vehicle Registration No.</label>
                                <InputText
                                    id="registrationNo"
                                    name="registrationNo"
                                    onBlur={formik.handleBlur}
                                    value={formik.values.registrationNo}
                                    onChange={formik.handleChange}
                                    className={formik.errors.registrationNo ? 'p-invalid' : ''}
                                    disabled={isViewMode}
                                />
                                {getFormErrorMessage('registrationNo')}
                            </div>
                            <div className="form-group">
                                <label className="form-label">Engine No.</label>
                                <InputText id="engineNo" name="engineNo" onBlur={formik.handleBlur} value={formik.values.engineNo} onChange={formik.handleChange} className={formik.errors.engineNo ? 'p-invalid' : ''} disabled={isViewMode} />
                                {getFormErrorMessage('engineNo')}
                            </div>
                            <div className="form-group">
                                <label className="form-label">Chassis No.</label>
                                <InputText id="chassisNo" name="chassisNo" onBlur={formik.handleBlur} value={formik.values.chassisNo} onChange={formik.handleChange} className={formik.errors.chassisNo ? 'p-invalid' : ''} disabled={isViewMode} />

                                {getFormErrorMessage('chassisNo')}
                            </div>
                        </div>

                        <div className="grid-3">
                            <div className="form-group">
                                <label className="form-label">Model</label>
                                <Dropdown
                                    id="model"
                                    name="model"
                                    value={formik.values.model}
                                    options={model.map((year) => ({ label: year.toString(), value: year }))}
                                    optionLabel="label"
                                    onBlur={formik.handleBlur}
                                    optionValue="label"
                                    onChange={formik.handleChange}
                                    className={formik.errors.model ? 'p-invalid' : ''}
                                    disabled={isViewMode}
                                />
                                {getFormErrorMessage('model')}
                            </div>
                            <div className="form-group">
                                <label className="form-label">Maker</label>
                                <InputText
                                    id="maker"
                                    name="maker"
                                    onBlur={formik.handleBlur}
                                    value={formik.values.maker}
                                    onChange={formik.handleChange}
                                    className={formik.errors.maker ? 'p-invalid' : ''}
                                    placeholder="Enter vehicle maker"
                                    disabled={isViewMode}
                                />
                                {getFormErrorMessage('maker')}
                            </div>
                            <div className="form-group">
                                <label className="form-label">Date</label>
                                <InputText type="date" id="date" onBlur={formik.handleBlur} name="date" value={formik.values.date} onChange={formik.handleChange} className={formik.errors.date ? 'p-invalid' : ''} disabled={isViewMode} />
                                {getFormErrorMessage('date')}
                            </div>
                        </div>
                    </div>

                    <div className="form-section">
                        <h3 className="section-title">Documents Attached</h3>
                        <div className="grid-3">
                            <div className="form-group">
                                <label className="form-label">CNIC Front</label>
                                <div className="document-upload" disabled={isViewMode}>
                                    <input
                                        type="file"
                                        accept=".pdf,.jpg,.jpeg,.png"
                                        onChange={(e) => {
                                            handleFileChange(e, 'cnicFrontSide');
                                            setCnicFrontSide(e.target.files[0]);
                                        }}
                                        style={{ display: 'none' }}
                                        onBlur={formik.handleBlur}
                                        id="cnicFrontSide"
                                        disabled={isViewMode}
                                    />
                                    <label htmlFor="cnicFrontSide" onBlur={formik.handleBlur} className={formik.errors.cnicFrontSide ? 'p-invalid' : ''}>
                                        <i className="pi pi-upload"></i>
                                        {formik.values.cnicFrontSideName || 'Upload CNIC Front'}
                                    </label>
                                    {/* {formik.errors.cnicFrontSide && <small className="p-error">{formik.errors.cnicFrontSide}</small>}
                                     */}
                                    {getFormErrorMessage('cnicFrontSide')}
                                </div>
                            </div>
                            <div className="form-group">
                                <label className="form-label">CNIC Back</label>
                                <div className="document-upload" disabled={isViewMode}>
                                    <input
                                        type="file"
                                        accept=".pdf,.jpg,.jpeg,.png"
                                        onChange={(e) => {
                                            handleFileChange(e, 'cnicBackSide');
                                            setCnicBackSide(e.target.files[0]);
                                        }}
                                        style={{ display: 'none' }}
                                        id="cnicBackSide"
                                        onBlur={formik.handleBlur}
                                        disabled={isViewMode}
                                    />
                                    <label htmlFor="cnicBackSide" onBlur={formik.handleBlur} className={formik.errors.cnicBackSide ? 'p-invalid' : ''}>
                                        <i className="pi pi-upload"></i>
                                        {formik.values.cnicBackSideName || 'Upload CNIC Back'}
                                    </label>
                                    {getFormErrorMessage('cnicBackSide')}
                                </div>
                            </div>
                            <div className="form-group">
                                <label className="form-label">Vehicle Registration Book</label>
                                <div className="document-upload" disabled={isViewMode}>
                                    <input
                                        type="file"
                                        accept=".pdf,.jpg,.jpeg,.png"
                                        onChange={(e) => {
                                            handleFileChange(e, 'vehicleRegistration');
                                        }}
                                        style={{ display: 'none' }}
                                        onBlur={formik.handleBlur}
                                        id="vehicleRegistration"
                                        disabled={isViewMode}
                                    />
                                    <label htmlFor="vehicleRegistration" onBlur={formik.handleBlur} className={formik.errors.vehicleRegistration ? 'p-invalid' : ''}>
                                        <i className="pi pi-upload"></i>
                                        {formik.values.vehicleRegistrationName || 'Upload Registration Book'}
                                    </label>
                                    {getFormErrorMessage('vehicleRegistration')}
                                </div>
                            </div>
                        </div>
                        <div className="grid-3">
                            <div className="form-group">
                                <label className="form-label">Fitness Certificate</label>
                                <div className="document-upload" disabled={isViewMode}>
                                    <input
                                        type="file"
                                        accept=".pdf,.jpg,.jpeg,.png"
                                        onChange={(e) => {
                                            handleFileChange(e, 'fitnessCertificate');
                                        }}
                                        style={{ display: 'none' }}
                                        id="fitnessCertificate"
                                        onBlur={formik.handleBlur}
                                        disabled={isViewMode}
                                    />
                                    <label htmlFor="fitnessCertificate" onBlur={formik.handleBlur} className={formik.errors.fitnessCertificate ? 'p-invalid' : ''}>
                                        <i className="pi pi-upload"></i>
                                        {formik.values.fitnessCertificateName || 'Upload Certificate'}
                                    </label>
                                    {getFormErrorMessage('fitnessCertificate')}
                                </div>
                            </div>
                            <div className="form-group">
                                <label className="form-label">Token</label>
                                <div className="document-upload" disabled={isViewMode}>
                                    <input
                                        type="file"
                                        accept=".pdf,.jpg,.jpeg,.png"
                                        onChange={(e) => {
                                            handleFileChange(e, 'token');
                                        }}
                                        style={{ display: 'none' }}
                                        id="token"
                                        onBlur={formik.handleBlur}
                                        disabled={isViewMode}
                                    />
                                    <label htmlFor="token" className={formik.errors.token ? 'p-invalid' : ''}>
                                        <i className="pi pi-upload"></i>
                                        {formik.values.tokenName || 'Upload Token'}
                                    </label>
                                    {getFormErrorMessage('token')}
                                </div>
                            </div>
                            <div className="form-group">
                                <label className="form-label">Affidavit</label>
                                <div className="document-upload" disabled={isViewMode}>
                                    <input
                                        type="file"
                                        accept=".pdf,.jpg,.jpeg,.png"
                                        onChange={(e) => {
                                            handleFileChange(e, 'affidavit');
                                        }}
                                        style={{ display: 'none' }}
                                        id="affidavit"
                                        onBlur={formik.handleBlur}
                                        disabled={isViewMode}
                                    />
                                    <label htmlFor="affidavit" className={formik.errors.affidavit ? 'p-invalid' : ''}>
                                        <i className="pi pi-upload"></i>
                                        {formik.values.affidavitName || 'Upload Affidavit'}
                                    </label>
                                    {getFormErrorMessage('affidavit')}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="form-section">
                        <h3 className="section-title">Fee Details</h3>
                        <div className="grid-3">
                            <div className="form-group">
                                <label className="form-label">Permit Fee</label>
                                <InputText
                                    id="permitFee"
                                    name="permitFee"
                                    value={formik.values.permitFee}
                                    onBlur={formik.handleBlur}
                                    onChange={formik.handleChange}
                                    className={formik.errors.permitFee ? 'p-invalid' : ''}
                                    placeholder="5000"
                                    disabled={isViewMode}
                                />
                                {getFormErrorMessage('permitFee')}
                            </div>
                            <div className="form-group">
                                <label className="form-label">Permit Fine</label>
                                <InputText
                                    id="permitFine"
                                    onBlur={formik.handleBlur}
                                    name="permitFine"
                                    value={formik.values.permitFine}
                                    onChange={formik.handleChange}
                                    className={formik.errors.permitFine ? 'p-invalid' : ''}
                                    placeholder="0"
                                    disabled={isViewMode}
                                />
                                {getFormErrorMessage('permitFine')}
                            </div>
                            <div className="form-group">
                                <label className="form-label">Total Permit Fee</label>
                                <InputText
                                    id="totalPermitFee"
                                    onBlur={formik.handleBlur}
                                    value={Number(formik.values.permitFee || 0) + Number(formik.values.permitFine || 0)}
                                    readOnly
                                    className={formik.errors.totalPermitFee ? 'p-invalid' : 'bg-gray-100'}
                                    disabled={isViewMode}
                                />
                                {getFormErrorMessage('totalPermitFee')}
                            </div>
                        </div>
                    </div>

                    {!isViewMode && (
                        <div className="flex justify-content-center">
                            <Button {...getSubmitButtonProps()} className="submit-button" disabled={isViewMode ? false : !formik.isValid || !formik.dirty} />
                        </div>
                    )}
                    {roleName === 'Fitness Officer' && isViewMode && (
                        <div className="flex justify-content-center">
                            {(status === 'Rejected' || status === 'Pending') && <Button label={status === 'Rejected' ? 'Rejected' : 'Reject'} className="rej-button" disabled={status !== 'Pending'} onClick={handleUpdateStatus('Rejected')} />}
                            {(status === 'Approved' || status === 'Pending') && <Button label={status === 'Approved' ? 'Approved' : 'Approve'} className="approve-button" disabled={status !== 'Pending'} onClick={handleUpdateStatus('Approved')} />}
                        </div>
                    )}
                </form>
            </div>
        </div>
    );
}

export default AddEditPermitRenewal;
